import React, { useCallback, useEffect, useRef, useState } from "react";
import * as E from "./style";
import * as C from "../../common-styles";
import * as H from "../Home/style";
import { TbSearch } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../Redux/store";
import {
  clearExploreResults,
  fetchExploreList,
} from "../../Redux/slices/exploreSlice";
import { RoleTypes } from "../../Constant/enums/roleTypes";
import Suggesstion from "../../Components/Suggesstion";
import CreatorSkeleton from "../../Components/Skeletons/CreatorSkeleton";
import { ExploreList } from "../../Config/interface.config";
import { checkIsSubscribe, isCreatorRole } from "../../Config/common.config";
import SubscriptionModal from "../../Modals/SubscriptionModal";
import { useLocation } from "react-router-dom";
import { usePullToRefresh } from "../../hooks/usePullToRefresh";

/* ---------------- CONSTANTS ---------------- */
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const Explore: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();

  const user = useSelector((state: RootState) => state.user);
  const explore = useSelector((state: RootState) => state.explore);
  const { loading, results, page, hasMore } = explore;
  const visibleCreators = results.filter(
    (creator) =>
      creator?.isSuggested !== false &&
      !checkIsSubscribe(creator?._id || creator?.id || "", user.subscribeTo as any),
  );

  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<ExploreList | null>(
    null,
  );

  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const resultsRootRef = useRef<HTMLDivElement | null>(null);

  /* ---------------- FETCH (SAFE & DEDUPED) ---------------- */
  const fetchCreators = useCallback(
    (pageToFetch: number, append = false, searchText = search) => {
      if (loading) return;
      if (pageToFetch !== 1 && !hasMore) return;

      dispatch(
        fetchExploreList({
          limit: PAGE_SIZE,
          page: pageToFetch,
          isAppend: append,
          fullname: searchText || undefined,
          isFilterBySubscriber: true,
          role: RoleTypes.CREATOR,
        }),
      );
    },
    [dispatch, loading, hasMore, search],
  );

  /* ---------------- INIT / NAVIGATION RESET ---------------- */
  useEffect(() => {
    dispatch(clearExploreResults());
    fetchCreators(1);
  }, [dispatch, location.pathname]);

  /* ---------------- SEARCH (DEBOUNCED) ---------------- */
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      dispatch(clearExploreResults());
      fetchCreators(1, false, value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  /* ---------------- INFINITE SCROLL ---------------- */
  useEffect(() => {
    if (!observerRef.current || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading && results.length > 0) {
          fetchCreators(page + 1, true);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [page, hasMore, loading, results.length, fetchCreators]);

  /* ---------------- SUBSCRIBE ---------------- */
  const onSubscribe = (creator: ExploreList) => {
    if (!user?.id) return;
    setSelectedCreator(creator);
    setOpen(true);
  };
  const refreshCreators = async () => {
    if (loading) return;
    dispatch(clearExploreResults());
    await dispatch(
      fetchExploreList({
        limit: PAGE_SIZE,
        page: 1,
        isAppend: false,
        fullname: search.trim() || undefined,
        isFilterBySubscriber: true,
        role: RoleTypes.CREATOR,
      }),
    );
  };

  const explorePullToRefresh = usePullToRefresh(
    resultsRootRef,
    refreshCreators,
    loading,
  );


  /* ---------------- RENDER ---------------- */
  return (
    <E.Explore>
      <E.Header>
        <C.IconInputWrapper className="input-wrapper">
          <C.CommonInput
            className="explore-input"
            placeholder="Search Creators"
            value={search}
            onChange={onSearchChange}
          />
          <TbSearch className="input-icon" size={18} />
        </C.IconInputWrapper>
        <h1>Suggestions for you</h1>
      </E.Header>

      <E.ProfileWrapper
        ref={resultsRootRef}
        onTouchStart={explorePullToRefresh.onTouchStart}
        onTouchMove={explorePullToRefresh.onTouchMove}
        onTouchEnd={explorePullToRefresh.onTouchEnd}
        onTouchCancel={explorePullToRefresh.onTouchCancel}
        style={{ overflowY: "auto", maxHeight: "calc(100vh - 100px)" }}
      >
        <H.PullToRefreshIndicator
          $offset={explorePullToRefresh.pullDistance}
          $refreshing={explorePullToRefresh.refreshing}
        >
          {explorePullToRefresh.refreshing ? "Refreshing creators..." : explorePullToRefresh.readyToRefresh ? "Release to refresh" : "Pull down to refresh"}
        </H.PullToRefreshIndicator>
        {/* EMPTY STATE */}
        {!loading && results.length === 0 && (
          <p className="text-white mt-5 text-center">No creators found</p>
        )}

        {/* RESULTS */}
        {visibleCreators.map((creator, index) => (
          <div key={creator.id ?? index} className="mb-10">
            <Suggesstion creator={creator} subscribeCreator={onSubscribe} />
          </div>
        ))}

        {/* LOADING */}
        {loading &&
          Array.from({ length: 3 }).map((_, i) => (
            <CreatorSkeleton key={`sk-${i}`} />
          ))}

        {/* SCROLL SENTINEL */}
        <div ref={observerRef} style={{ height: 40 }} />

        {/* END */}
        {!loading && !hasMore && results.length > 0 && (
          <p className="text-white mt-5 text-center">
            You've reached the end 🎉
          </p>
        )}
      </E.ProfileWrapper>

      {!isCreatorRole(user.role) && (
        <SubscriptionModal
          selectedCreator={selectedCreator}
          onHide={() => setOpen(false)}
          show={open}
          setRefreshFeedsWithCreatorList={() => {}}
        />
      )}
    </E.Explore>
  );
};

export default Explore;
